import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateRolePayload = {
  name: string;
  permission_codes: string[];
};

export type UpdateRolePayload = CreateRolePayload & {
  id: number;
};

export type Role = {
  id: number;
  name: string;
  permissions: string[];
  created_at: string;
};

export type RolesState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  roles: Role[];
  permissionsStatus: "idle" | "loading" | "succeeded" | "failed";
  permissions: string[];
};

export const rolesInitialState: RolesState = {
  status: STATUS.IDLE,
  error: null,
  roles: [],
  permissionsStatus: STATUS.IDLE,
  permissions: [],
};

function isRole(value: unknown): value is Role {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    Array.isArray(row.permissions) &&
    row.permissions.every((permission) => typeof permission === "string") &&
    typeof row.created_at === "string"
  );
}

function parseRolesResponse(data: unknown): Role[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isRole)) {
    return null;
  }
  return data;
}

function parsePermissionsResponse(data: unknown): string[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every((code) => typeof code === "string")) {
    return null;
  }
  return data;
}

export const fetchRoles = createAsyncThunk<
  Role[],
  void,
  { rejectValue: string }
>("roles/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/roles", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const roles = parseRolesResponse(data);
    if (!roles) {
      return rejectWithValue("Unexpected response shape");
    }
    return roles;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createRole = createAsyncThunk<
  Role,
  CreateRolePayload,
  { rejectValue: string }
>("roles/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/roles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isRole(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const updateRole = createAsyncThunk<
  Role,
  UpdateRolePayload,
  { rejectValue: string }
>("roles/update", async ({ id, ...payload }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/roles/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isRole(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchPermissions = createAsyncThunk<
  string[],
  void,
  { rejectValue: string }
>("roles/fetchPermissions", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/roles/permissions", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const permissions = parsePermissionsResponse(data);
    if (!permissions) {
      return rejectWithValue("Unexpected response shape");
    }
    return permissions;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const rolesSlice = createSlice({
  name: "roles",
  initialState: rolesInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.roles = [];
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const index = state.roles.findIndex((role) => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(fetchPermissions.pending, (state) => {
        state.permissionsStatus = STATUS.LOADING;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissionsStatus = STATUS.SUCCEEDED;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state) => {
        state.permissionsStatus = STATUS.FAILED;
        state.permissions = [];
      });
  },
});

export const rolesReducer = rolesSlice.reducer;

export const selectRoles = (state: RootState) => state.roles.roles;
export const selectRolesStatus = (state: RootState) => state.roles.status;
export const selectPermissions = (state: RootState) => state.roles.permissions;
export const selectPermissionsStatus = (state: RootState) => state.roles.permissionsStatus;
